// ────────────────────────────────────────
// 바이낸스 kline WebSocket — 코인 초단위 실시간 (2026-06-13)
// - 클라이언트(WebView) 직결: 공개 시장 데이터 무료·서버 부하 0·WS는 CORS 미적용
// - kline 스트림 = 진행 중 봉의 o/h/l/c/v를 초당 1~2회 푸시 → "자라는 봉 + 거래량" 증권사 경험
// - 가격 축: USDT 페어 ≈ USD (Yahoo BTC-USD 봉과 수달러 단차 가능 — 봉 교체 시 ohlcv가 정리)
// - 체결가는 서버 quotePrice(Yahoo) 기준 유지 — 표시 시세와 체결가 분리는 증권사도 동일
// ────────────────────────────────────────
// 순수 파싱·병합·가격 반영은 @core/sim/market/liveParsers에 둔다.
import { binanceInterval, binanceSymbol, parseKline, type LiveCandle } from '@core/sim/market/liveParsers';

/** 바이낸스 외부 kline 엔드포인트 문자열 — 외부 제공자 명세이므로 adapter 소속 */
export function klineStreamUrl(pair: string, interval: string): string {
  return `wss://stream.binance.com:9443/ws/${pair}@kline_${interval}`;
}

/** miniTicker combined 스트림 — 보유 코인 여러 종목의 현재가만 구독 (kline보다 가벼움, ~1초 주기) */
export function subscribeMiniTickers(
  symbols: string[],
  onPrice: (symbol: string, price: number) => void,
): () => void {
  const pairs = symbols
    .map((s) => ({ orig: s, pair: binanceSymbol(s) }))
    .filter((x): x is { orig: string; pair: string } => !!x.pair);
  if (!pairs.length) return () => {};
  const byUpper: Record<string, string> = {};
  for (const p of pairs) byUpper[p.pair.toUpperCase()] = p.orig;
  const streams = pairs.map((p) => `${p.pair}@miniTicker`).join('/');
  const url = `wss://stream.binance.com:9443/stream?streams=${streams}`;

  let ws: WebSocket | null = null;
  let closed = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  const connect = () => {
    if (closed) return;
    try {
      ws = new WebSocket(url);
    } catch {
      scheduleRetry();
      return;
    }
    ws.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data)?.data; // combined: { stream, data }
        const orig = d?.s ? byUpper[d.s] : null;
        const price = Number(d?.c);
        if (orig && Number.isFinite(price) && price > 0) onPrice(orig, price);
      } catch { /* 형식 밖 무시 */ }
    };
    ws.onclose = () => { if (!closed) scheduleRetry(); };
    ws.onerror = () => { try { ws?.close(); } catch { /* noop */ } };
  };
  const scheduleRetry = () => {
    if (closed || retryTimer) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (document.visibilityState === 'visible') connect();
      else scheduleRetry();
    }, 3000);
  };
  connect();
  return () => {
    closed = true;
    if (retryTimer) clearTimeout(retryTimer);
    try { ws?.close(); } catch { /* noop */ }
  };
}

/**
 * kline 구독 — onCandle로 실시간 봉 전달. 반환값 호출 = 구독 해제.
 * - 비정상 종료 시 3초 후 자동 재연결 (화면 가시 상태에서만)
 * - 코인 아닌 심볼/미지원 interval이면 no-op 해제 함수 반환 (호출부 분기 불필요)
 */
export function subscribeKline(
  symbol: string,
  yahooInterval: string,
  onCandle: (c: LiveCandle) => void,
): () => void {
  const pair = binanceSymbol(symbol);
  const interval = binanceInterval(yahooInterval);
  if (!pair || !interval) return () => {};

  let ws: WebSocket | null = null;
  let closed = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  const connect = () => {
    if (closed) return;
    try {
      ws = new WebSocket(klineStreamUrl(pair, interval));
    } catch {
      scheduleRetry();
      return;
    }
    ws.onmessage = (ev) => {
      try {
        const c = parseKline(JSON.parse(ev.data));
        if (c) onCandle(c);
      } catch { /* 형식 밖 메시지 무시 */ }
    };
    ws.onclose = () => { if (!closed) scheduleRetry(); };
    ws.onerror = () => { try { ws?.close(); } catch { /* noop */ } };
  };
  const scheduleRetry = () => {
    if (closed || retryTimer) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (document.visibilityState === 'visible') connect();
      else scheduleRetry(); // 백그라운드면 다음 틱으로 미룸 (복귀 후 연결)
    }, 3000);
  };
  connect();
  return () => {
    closed = true;
    if (retryTimer) clearTimeout(retryTimer);
    try { ws?.close(); } catch { /* noop */ }
  };
}
